import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Typography } from '@mui/material';

import type { CapacityCombination, RoomRateOccupancyPayload } from './utils';

export type RoomRateCapacityRow = {
  capacityCombinations?: Array<CapacityCombination>;
  occupancyPayload?: RoomRateOccupancyPayload;
  roomName: string;
  roomRateId: string;
  roomTypeId: string;
};

interface Props {
  capacityRows: Array<RoomRateCapacityRow>;
  readOnly?: boolean;
}

const MAX_FIELDS: Array<{ key: keyof RoomRateOccupancyPayload; label: string }> = [
  { key: 'max_occupancy', label: 'Max guests' },
  { key: 'max_adult_occupancy', label: 'Max adults' },
  { key: 'max_child_occupancy', label: 'Max children' },
  { key: 'max_infant_occupancy', label: 'Max infants' },
  { key: 'max_teenager_occupancy', label: 'Max teenagers' },
];

const COMBINATION_LABELS = ['Adults', 'Children', 'Infants', 'Teenagers'] as const;

export default function RoomRateCapacityStep({ capacityRows, readOnly = false }: Props) {
  if (capacityRows.length === 0) {
    return <Alert severity="info">No newly created room rates need capacity applied.</Alert>;
  }

  return (
    <Stack spacing={2}>
      {readOnly && <Alert severity="success">Capacity has been applied to the matched room rates.</Alert>}

      {capacityRows.map((capacityRow) => {
        const occupancyPayload = capacityRow.occupancyPayload;
        const combinations = capacityRow.capacityCombinations ?? [];

        return (
          <Accordion
            key={capacityRow.roomRateId}
            disableGutters
            defaultExpanded
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              boxShadow: 'none',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">{capacityRow.roomName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {combinations.length} included guest {combinations.length === 1 ? 'row' : 'rows'}
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
              <Stack spacing={1.25}>
                {occupancyPayload ? (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' },
                        gap: 1.25,
                      }}
                    >
                      {MAX_FIELDS.map((field) => (
                        <Box
                          key={field.key}
                          sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            bgcolor: 'background.default',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {field.label}
                          </Typography>
                          <Typography variant="body2">{occupancyPayload[field.key]}</Typography>
                        </Box>
                      ))}
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          bgcolor: 'background.default',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Include infants
                        </Typography>
                        <Typography variant="body2">{occupancyPayload.include_infants ? 'Yes' : 'No'}</Typography>
                      </Box>
                    </Box>

                    <Stack spacing={0.75}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1 }}>
                        Room rate included guests
                      </Typography>

                      {combinations.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No included guest combinations.
                        </Typography>
                      )}

                      {combinations.map((combination, index) => (
                        <Box
                          key={`${combination.join('-')}-${index}`}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1.25,
                            alignItems: 'center',
                            py: 0.25,
                          }}
                        >
                          {COMBINATION_LABELS.map((label, labelIndex) => (
                            <Box
                              key={label}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto',
                                gap: 2,
                                pr: 2,
                                borderRight: { md: labelIndex === COMBINATION_LABELS.length - 1 ? 0 : 1 },
                                borderColor: 'divider',
                              }}
                            >
                              <Typography variant="body2">{label}</Typography>
                              <Typography variant="body2">{combination[labelIndex]}</Typography>
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body2" color="warning.main">
                    No Salesforce room asset capacity found
                  </Typography>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
